import React, { useState, useEffect } from 'react';
import { getTexts } from '../config/texts';
import { getUserPoints, getPointPackages, createRechargeOrder } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './PointsRecharge.css';

const PointsRecharge = ({ language, setCurrentPage }) => {
  const { user } = useAuth();
  const t = getTexts('pointsRecharge', language);

  const [points, setPoints] = useState(0);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState('paypal'); // paypal or stripe
  const [processing, setProcessing] = useState(false);

  // 加载积分和套餐
  const loadData = async () => {
    setLoading(true);
    try {
      // 加载用户积分
      const pointsResult = await getUserPoints();
      if (pointsResult.code === 200) {
        setPoints(pointsResult.data?.available_points || 0);
      }

      // 加载套餐列表
      const packagesResult = await getPointPackages();
      if (packagesResult.code === 200) {
        setPackages(packagesResult.data?.packages || []);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  // 选择套餐
  const handleSelectPackage = (pkg) => {
    setSelectedPackage(pkg);
  };

  // 选择支付方式
  const handleSelectPayment = (method) => {
    setSelectedPayment(method);
  };

  // 充值
  const handleRecharge = async () => {
    if (!selectedPackage) {
      alert(t.selectPackage || '请选择充值套餐');
      return;
    }

    setProcessing(true);
    try {
      const result = await createRechargeOrder(selectedPackage.id, selectedPayment);

      if (result.code === 200) {
        const { order, payment_data } = result.data;

        if (selectedPayment === 'paypal' && payment_data?.approval_url) {
          // PayPal 支付：跳转到 PayPal
          window.location.href = payment_data.approval_url;
        } else if (selectedPayment === 'stripe' && payment_data?.client_secret) {
          // Stripe 支付：需要使用 Stripe.js 处理
          handleStripePayment(payment_data);
        } else {
          alert(t.unsupportedPayment || '暂不支持该支付方式');
        }
      } else {
        alert(result.message || (t.createOrderFailed || '创建订单失败'));
      }
    } catch (error) {
      console.error('Recharge error:', error);
      alert(t.networkError || '网络请求失败，请重试');
    } finally {
      setProcessing(false);
    }
  };

  // 处理 Stripe 支付
  const handleStripePayment = (paymentData) => {
    // 这里需要集成 Stripe.js
    // 暂时提示用户
    alert('Stripe 支付功能正在开发中，请使用 PayPal 支付');
  };

  if (loading) {
    return (
      <div className="points-recharge-container">
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <p>{t.loading || '加载中...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="points-recharge-container">
      <div className="points-header">
        <div className="points-info">
          <i className="fas fa-coins"></i>
          <span className="points-label">{t.currentPoints || '当前积分'}：</span>
          <span className="points-value">{points}</span>
        </div>
        <button
          className="view-orders-btn"
          onClick={() => setCurrentPage && setCurrentPage('orders')}
        >
          <i className="fas fa-receipt"></i>
          <span>{t.myOrders || '我的订单'}</span>
        </button>
      </div>

      <div className="packages-section">
        <h3>{t.selectPackage || '选择充值套餐'}</h3>
        <div className="packages-grid">
          {packages.map(pkg => (
            <div
              key={pkg.id}
              className={`package-card ${selectedPackage?.id === pkg.id ? 'selected' : ''}`}
              onClick={() => handleSelectPackage(pkg)}
            >
              <div className="package-points">{pkg.points}</div>
              <div className="package-label">{t.points || '积分'}</div>
              {pkg.actual_price !== undefined ? (
                <>
                  {/* 显示折后价 */}
                  <div className="package-price">
                    {pkg.currency === 'CNY' ? '¥' : '$'}{pkg.actual_price}
                  </div>
                  {/* 如果有折扣，显示原价和折扣标签 */}
                  {pkg.price && pkg.price > pkg.actual_price && (
                    <>
                      <div className="package-original-price">
                        {pkg.currency === 'CNY' ? '¥' : '$'}{pkg.price}
                      </div>
                      <div className="package-discount">
                        {Math.round((1 - pkg.actual_price / pkg.price) * 100)}% OFF
                      </div>
                    </>
                  )}
                </>
              ) : (
                /* 兼容旧数据：如果没有 actual_price，显示 price */
                <div className="package-price">
                  {pkg.currency === 'CNY' ? '¥' : '$'}{pkg.price}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {selectedPackage && (
        <div className="payment-section">
          <h3>{t.selectPayment || '选择支付方式'}</h3>
          <div className="payment-options">
            <button
              className={`payment-option ${selectedPayment === 'paypal' ? 'selected' : ''}`}
              onClick={() => handleSelectPayment('paypal')}
            >
              <i className="fab fa-paypal"></i>
              <span>PayPal</span>
            </button>
            <button
              className={`payment-option ${selectedPayment === 'stripe' ? 'selected' : ''}`}
              onClick={() => handleSelectPayment('stripe')}
            >
              <i className="fab fa-stripe-s"></i>
              <span>Stripe</span>
            </button>
          </div>

          <div className="order-summary">
            <div className="summary-item">
              <span>{t.package || '套餐'}：</span>
              <span>{selectedPackage.points} {t.points || '积分'}</span>
            </div>
            <div className="summary-item">
              <span>{t.amount || '金额'}：</span>
              <span>
                {selectedPackage.currency === 'CNY' ? '¥' : '$'}{selectedPackage.actual_price || selectedPackage.price}
              </span>
            </div>
          </div>

          <button
            className="recharge-btn"
            onClick={handleRecharge}
            disabled={processing}
          >
            {processing ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                <span>{t.processing || '处理中...'}</span>
              </>
            ) : (
              <>
                <i className="fas fa-credit-card"></i>
                <span>{t.recharge || '立即充值'}</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};

export default PointsRecharge;
